import React, { createContext, useState, useContext, useEffect } from 'react';
import { signInWithGoogle, signOut, onAuthChange } from '@/lib/firebaseAuth';

// Create auth context
const AuthContext = createContext(null);

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userDisplayName, setUserDisplayName] = useState('');
  const [userPhotoURL, setUserPhotoURL] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  // Load user data from localStorage on initial render
  useEffect(() => {
    const loadUserData = () => {
      const storedIsSignedIn = localStorage.getItem('isSignedIn') === 'true';
      const storedUserEmail = localStorage.getItem('userEmail');
      const storedUserDisplayName = localStorage.getItem('userDisplayName');
      const storedUserPhotoURL = localStorage.getItem('userPhotoURL');

      if (storedIsSignedIn && storedUserEmail && storedUserDisplayName) {
        setIsSignedIn(true);
        setUserEmail(storedUserEmail);
        setUserDisplayName(storedUserDisplayName);
        setUserPhotoURL(storedUserPhotoURL || '');
      }
      
      setIsLoading(false);
    };

    loadUserData();

    // Also listen for Firebase auth state changes
    const unsubscribe = onAuthChange((user) => {
      if (user) {
        // User is signed in
        saveUserData(user);
      }
    });

    return () => unsubscribe();
  }, []);

  // Save user data to localStorage
  const saveUserData = (user) => {
    const { email, displayName, photoURL } = user;
    
    console.log('User data from Google:', { email, displayName, photoURL });
    
    // Save to state
    setIsSignedIn(true);
    setUserEmail(email);
    setUserDisplayName(displayName || '');
    setUserPhotoURL(photoURL || '');
    
    // Save to localStorage
    localStorage.setItem('isSignedIn', 'true');
    localStorage.setItem('userEmail', email);
    localStorage.setItem('userDisplayName', displayName || '');
    localStorage.setItem('userPhotoURL', photoURL || '');
    
    console.log('Data saved to localStorage');
  };

  // Clear user data from localStorage
  const clearUserData = () => {
    // Clear state
    setIsSignedIn(false);
    setUserEmail('');
    setUserDisplayName('');
    setUserPhotoURL('');
    
    // Clear localStorage
    localStorage.removeItem('isSignedIn');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userDisplayName');
    localStorage.removeItem('userPhotoURL');
  };

  // Handle Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      const { user, error } = await signInWithGoogle();
      
      if (error) {
        console.error('Google Sign In failed', error);
        return { success: false, error };
      }
      
      if (user) {
        saveUserData(user);
        return { success: true, user };
      }
      
      return { success: false, error: 'No user returned' };
    } catch (err) {
      console.error('Unexpected error during sign in', err);
      return { success: false, error: err.message };
    }
  };

  // Handle Sign Out
  const handleSignOut = async () => {
    try {
      const { error } = await signOut();
      
      if (error) {
        console.error('Sign out failed', error);
        return { success: false, error };
      }
      
      clearUserData();
      return { success: true };
    } catch (err) {
      console.error('Unexpected error during sign out', err);
      return { success: false, error: err.message };
    }
  };

  // Create the context value object
  const value = {
    isSignedIn,
    userEmail,
    userDisplayName,
    userPhotoURL,
    isLoading,
    handleGoogleSignIn,
    handleSignOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext; 